import { useCallback, useMemo } from 'react';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useMobileDrawerContext } from 'src/contexts/MobileDrawerContext';
import {
  InventoryListingSortingCriteria,
  InventorySortingOrder,
  ListingSortByFields,
  ListingSortByFieldsToContentId,
  ListingSortDisplayConfig,
  ListingSortDisplayConfigToContentId,
  useMobileInventorySortAndFilterContext,
} from 'src/contexts/MobileSortAndFilterContext';
import { PosSelect } from 'src/core/POS/PosSelect';
import { vars } from 'src/core/themes';
import { Button, RadioGroup, Stack } from 'src/core/ui';
import { useComputedState } from 'src/hooks/useComputedState';
import { ContentId } from 'src/utils/constants/contentId';

import { InventoryConfigItem } from './components/InventoryConfigItem';
import { InventorySortingRadioItem } from './components/InventorySortingItem';
import * as styles from './InventoryListingSortingContent.css';

export const RIGHT_ARROW = '\u2192'; // Unicode right arrow (→)

export const InventoryListingSortingContent = ({
  isAllInventory,
}: {
  isAllInventory: boolean;
}) => {
  const { sortingCriteria, setSortingCriteria } =
    useMobileInventorySortAndFilterContext();
  const { toggle } = useMobileDrawerContext();
  const lowText = useContent(ContentId.Low);
  const highText = useContent(ContentId.High);

  const [localSortingCriteria, setLocalSortingCriteria] =
    useComputedState<InventoryListingSortingCriteria>(
      sortingCriteria as InventoryListingSortingCriteria
    );

  const onUpdateSortingField = useCallback(
    (field: ListingSortByFields) => {
      if (isAllInventory && field !== ListingSortByFields.Price) {
        return;
      }
      setLocalSortingCriteria((prev) => ({
        ...prev,
        sorting: { ...prev.sorting, sortBy: field, order: 'asc' },
      }));
    },
    [isAllInventory, setLocalSortingCriteria]
  );

  const onUpdateSortingOrder = useCallback(
    (order: InventorySortingOrder) => {
      setLocalSortingCriteria((prev) => ({
        ...prev,
        sorting: { ...prev.sorting, order },
      }));
    },
    [setLocalSortingCriteria]
  );

  const onUpdateConfigView = useCallback(
    (config: ListingSortDisplayConfig) => (isChecked: boolean) => {
      setLocalSortingCriteria((prev) => {
        const updated = new Set(prev.displayConfig.configs);
        if (isChecked) {
          updated.add(config);
        } else {
          updated.delete(config);
        }
        return { ...prev, displayConfig: { configs: Array.from(updated) } };
      });
    },
    [setLocalSortingCriteria]
  );

  const onSaveSortingCriteria = useCallback(() => {
    setSortingCriteria(localSortingCriteria);
    toggle();
  }, [localSortingCriteria, setSortingCriteria, toggle]);

  const sortingOrderOptionContent = useMemo((): Record<
    InventorySortingOrder,
    string
  > => {
    return {
      ['asc']: `${lowText} ${RIGHT_ARROW} ${highText}`,
      ['desc']: `${highText} ${RIGHT_ARROW} ${lowText}`,
    };
  }, [highText, lowText]);

  return (
    <Stack
      direction="column"
      width="full"
      height="full"
      alignItems="center"
      gap="m"
      style={{ color: vars.color.textPrimary }}
    >
      <Stack
        direction="column"
        width="full"
        gap="l"
        style={{ flex: 1, overflowY: 'auto', padding: vars.spacing.sm }}
      >
        <Stack direction="column" width="full" gap="m">
          <span className={styles.sortAndFilterContentHeader}>
            <Content id={ContentId.SortInventoryBy} />
          </span>
          <RadioGroup
            value={localSortingCriteria.sorting.sortBy}
            onValueChange={onUpdateSortingField}
          >
            <Stack direction="column" width="full" alignItems="center" gap="s">
              {Object.entries(ListingSortByFieldsToContentId).map(
                ([field, contentId]) => {
                  const disabled =
                    isAllInventory && field !== ListingSortByFields.Price;
                  const ordering =
                    localSortingCriteria.sorting.sortBy === field
                      ? localSortingCriteria.sorting.order
                      : 'asc';
                  return (
                    <InventorySortingRadioItem
                      key={contentId}
                      contentId={contentId}
                      disabled={disabled}
                      isDefault={field === ListingSortByFields.Price}
                      sortingField={field as ListingSortByFields}
                      rightContent={
                        localSortingCriteria.sorting.sortBy !==
                        field ? undefined : (
                          <PosSelect
                            className={styles.sortingOrderTrigger}
                            disabled={disabled}
                            value={ordering}
                            onChange={(newOrder: string) => {
                              onUpdateSortingOrder(
                                newOrder as InventorySortingOrder
                              );
                            }}
                            valueOptionsContent={{
                              ['asc']: '',
                              ['desc']: '',
                            }}
                            valueOptionsContentOverride={
                              sortingOrderOptionContent
                            }
                          />
                        )
                      }
                      onClick={() =>
                        onUpdateSortingField(field as ListingSortByFields)
                      }
                    />
                  );
                }
              )}
            </Stack>
          </RadioGroup>
        </Stack>
        <Stack direction="column" width="full" gap="m">
          <span className={styles.sortAndFilterContentHeader}>
            <Content id={ContentId.ConfigureView} />
          </span>
          <Stack direction="column" width="full" alignItems="center" gap="s">
            {Object.entries(ListingSortDisplayConfigToContentId).map(
              ([config, contentId]) => {
                return (
                  <InventoryConfigItem
                    key={contentId}
                    contentId={contentId}
                    disabled={
                      !isAllInventory &&
                      (config === ListingSortDisplayConfig.HideAllDefects ||
                        config === ListingSortDisplayConfig.HideAllFanInventory)
                    }
                    isChecked={localSortingCriteria.displayConfig.configs.includes(
                      config as ListingSortDisplayConfig
                    )}
                    onUpdateConfig={onUpdateConfigView(
                      config as ListingSortDisplayConfig
                    )}
                  />
                );
              }
            )}
          </Stack>
        </Stack>
      </Stack>
      <Stack justifyContent="spaceBetween" width="full" alignItems="center">
        <Button
          style={{ height: '40px' }}
          shape="rect"
          variant="outlineGray"
          onClick={toggle}
        >
          <Content id={ContentId.Cancel} />
        </Button>
        <Button
          style={{ height: '40px' }}
          shape="rect"
          onClick={onSaveSortingCriteria}
        >
          <Content id={ContentId.Save} />
        </Button>
      </Stack>
    </Stack>
  );
};
